import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Assignment,
  Assessment,
  People,
  Warning,
  CheckCircle,
  Schedule,
  TrendingUp,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  getManagerEvaluationListPath,
  getManagerEvaluationFormPath,
  getDeptHeadEvaluationListPath,
  getDeptHeadEvaluationFormPath,
  GOAL_SETTING_PATH,
  GOAL_APPROVAL_PATH,
} from '../../utils/routes';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import {
  getStatusLabel,
  EVALUATION_FUNNEL_STAGES,
  getFunnelStageCount,
  type EvaluationStatus,
  type GoalApprovalStatus
} from '../../utils/statusLabels';
import { extractErrorMessage } from '../../utils/errorHandler';
import { formatDateJST, formatDateTimeJST, todayStringJST } from '../../utils/dateUtils';

interface DashboardStats {
  hasActivePeriod: boolean;
  activePeriod?: {
    period_id: string;
    period_name: string;
    start_date: string;
    end_date: string;
    status: string;
    goal_setting_start?: string | null;
    goal_setting_end?: string | null;
    mid_term_review_start?: string | null;
    mid_term_review_end?: string | null;
    self_eval_start?: string | null;
    self_eval_end?: string | null;
    manager_eval_start?: string | null;
    manager_eval_end?: string | null;
    dept_eval_start?: string | null;
    dept_eval_end?: string | null;
    hr_review_start?: string | null;
    hr_review_end?: string | null;
    days_until_deadline: number | null;
    deadline_phase: string | null;
    has_evaluation_role: boolean;
  };
  selfEvaluation?: {
    evaluation_id: string;
    status: string;
    updated_at: string;
  };
  managerEvaluations: any[];
  deptHeadEvaluations: any[];
  overallProgress?: {
    total_evaluations: number;
    not_started_count: number;
    root_defined_count: number;
    goal_setting_count: number;
    self_evaluated_count: number;
    manager_evaluated_count: number;
    dept_evaluated_count: number;
    r_applied_count: number;
    finalized_count: number;
  };
  goal?: {
    approval_status: string;
    submitted_at?: string;
    updated_at?: string;
    target_description?: string;
    achievement_criteria?: string;
    rejection_reason?: string | null;
    revision_phase?: string;
    version?: number;
  } | null;
  counts: {
    pending_manager_evaluations: number;
    pending_dept_head_evaluations: number;
    pending_goal_approvals?: number;
  };
  flags?: {
    can_approve_goals?: boolean;
  };
}

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, evaluatorInfo } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [personalStats, setPersonalStats] = useState<any>(null);
  const [error, setError] = useState('');
  // 全体評価進捗（HR向け・期間切替対応）
  const [orgProgress, setOrgProgress] = useState<any>(null);
  const [orgPeriodId, setOrgPeriodId] = useState<string>('');

  useEffect(() => {
    fetchDashboardStats();
    fetchPersonalStats();
    if (user?.roles?.includes('hr')) {
      fetchOrgProgress();
    }
  }, [evaluatorInfo, user]);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await api.get('/dashboard/stats');

      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (err) {
      console.error('ダッシュボード統計取得エラー:', err);
      setError(extractErrorMessage(err, 'ダッシュボード統計の取得に失敗しました'));
    } finally {
      setLoading(false);
    }
  };

  const fetchPersonalStats = async () => {
    try {
      const response = await api.get('/dashboard-stats/personal');

      setPersonalStats(response.data);
    } catch (err) {
      console.error('個人統計取得エラー:', err);
    }
  };

  const fetchOrgProgress = async (periodId?: string) => {
    try {
      const response = await api.get('/reports/org-progress', {
        params: periodId ? { period_id: periodId } : {}
      });
      if (response.data.success) {
        setOrgProgress(response.data.data);
        if (response.data.data.period?.period_id) {
          setOrgPeriodId(response.data.data.period.period_id);
        }
      }
    } catch (err) {
      console.error('全社評価進捗取得エラー:', err);
    }
  };

  const handleOrgPeriodChange = (periodId: string) => {
    setOrgPeriodId(periodId);
    fetchOrgProgress(periodId);
  };


  // 全体進捗のファネル（段階別人数）を正規化して返す。
  // 期間切替に対応した org-progress を優先し、無ければ /stats のアクティブ期間分を使う。
  const getOverallFunnel = () => {
    if (orgProgress?.funnel) {
      return orgProgress.funnel;
    }
    if (stats?.overallProgress) {
      const o = stats.overallProgress;
      return {
        total: Number(o.total_evaluations) || 0,
        not_started: Number(o.not_started_count) || 0,
        root_defined: Number(o.root_defined_count) || 0,
        goal_setting: Number(o.goal_setting_count) || 0,
        self_evaluated: Number(o.self_evaluated_count) || 0,
        manager_evaluated: Number(o.manager_evaluated_count) || 0,
        dept_evaluated: Number(o.dept_evaluated_count) || 0,
        r_applied: Number(o.r_applied_count) || 0,
        finalized: Number(o.finalized_count) || 0
      };
    }
    return null;
  };

  const getProgressPercentage = () => {
    const f = getOverallFunnel();
    const total = Number(f?.total) || 0;
    if (total === 0) return 0;
    // 進捗率の分子は最終承認完了（finalized）のみ
    const finalized = Number(f?.finalized) || 0;
    return (finalized / total) * 100;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!stats) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  // アクティブな評価期間がない場合でも基本情報を表示
  const noActivePeriod = !stats.hasActivePeriod;

  const { activePeriod } = stats;
  // 評価ルートに設定されており、期限が設定されている場合のみ表示
  const hasEvaluationRole = activePeriod?.has_evaluation_role || false;
  const daysLeft = activePeriod?.days_until_deadline;
  const deadlinePhase = activePeriod?.deadline_phase;
  const showDeadlineAlert = hasEvaluationRole && daysLeft !== null && daysLeft !== undefined;
  const isOverdue = showDeadlineAlert && daysLeft < 0; // 期限切れ
  const isUrgent = showDeadlineAlert && daysLeft >= 0 && daysLeft <= 7; // 期限間近（7日以内）
  
  // フェーズに応じた期限ラベルを取得
  const getDeadlineLabel = (phase: string | null | undefined): string => {
    switch (phase) {
      case 'goal_setting':
        return '目標設定期限';
      case 'mid_term_goal_review':
        return '中期レビュー（目標改訂）期限';
      case 'goal_approval':
        return '目標承認期限';
      case 'self_evaluation':
        return '自己評価提出期限';
      case 'manager_evaluation':
        return '上長評価期限';
      case 'dept_evaluation':
        return '部門長評価期限';
      default:
        return '提出期限';
    }
  };
  
  // 期限メッセージを取得
  const getDeadlineMessage = (): string => {
    const label = getDeadlineLabel(deadlinePhase);
    if (isOverdue) {
      return `${label}が${Math.abs(daysLeft as number)}日過ぎています`;
    } else {
      return `${label}まで残り${daysLeft}日`;
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        ダッシュボード
      </Typography>

      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        ようこそ、{user?.name}さん
      </Typography>

      {/* アクティブな評価期間がない場合の表示 */}
      {noActivePeriod && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            現在アクティブな評価期間がありません
          </Typography>
          <Typography variant="body2">
            評価期間が開始されると、ここに評価に関する情報が表示されます。<br />
            システムは正常に稼働しています。
          </Typography>
        </Alert>
      )}

      {/* 評価期間と期限 */}
      {!noActivePeriod && activePeriod && (
        <Card sx={{ mb: 3, bgcolor: isOverdue ? '#ffebee' : isUrgent ? '#fff3e0' : 'background.paper' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Schedule sx={{ mr: 1 }} />
              <Typography variant="h6">評価期間: {activePeriod.period_name}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              期間: {formatDateJST(activePeriod.start_date)} ～{' '}
              {formatDateJST(activePeriod.end_date)}
            </Typography>
            {/* 各フェーズのプログレス（横ステッパー）＋現在フェーズ明記 */}
            {(() => {
              const phases = ([
                { label: '目標設定', start: activePeriod.goal_setting_start, end: activePeriod.goal_setting_end },
                { label: '中間レビュー', start: activePeriod.mid_term_review_start, end: activePeriod.mid_term_review_end },
                { label: '自己評価', start: activePeriod.self_eval_start, end: activePeriod.self_eval_end },
                { label: '上長評価', start: activePeriod.manager_eval_start, end: activePeriod.manager_eval_end },
                { label: '部門長評価', start: activePeriod.dept_eval_start, end: activePeriod.dept_eval_end },
                { label: 'HR確認', start: activePeriod.hr_review_start, end: activePeriod.hr_review_end },
              ] as const).filter((ph) => ph.start && ph.end) as { label: string; start: string; end: string }[];

              if (phases.length === 0) return null;

              const todayStr = todayStringJST();
              const statuses = phases.map((ph) => {
                const s = ph.start.slice(0, 10);
                const e = ph.end.slice(0, 10);
                if (todayStr > e) return 'completed';
                if (todayStr >= s) return 'current';
                return 'upcoming';
              });
              // 期間が重複している場合、該当する全フェーズを「実施中」とする
              const currentIndexes = statuses
                .map((st, i) => (st === 'current' ? i : -1))
                .filter((i) => i >= 0);
              const currentPhases = currentIndexes.map((i) => phases[i]);
              const firstCurrentIndex = currentIndexes.length > 0 ? currentIndexes[0] : -1;
              const currentLabels = currentPhases.map((p) => p.label).join('・');
              // 複数該当時は最も早く終わるフェーズの残日数を表示
              const earliestEnd =
                currentPhases.length > 0
                  ? currentPhases.map((p) => p.end.slice(0, 10)).sort()[0]
                  : null;
              const daysLeftInPhase = earliestEnd
                ? Math.ceil(
                    (new Date(earliestEnd).getTime() - new Date(todayStr).getTime()) /
                      (1000 * 60 * 60 * 24)
                  )
                : null;

              return (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ mb: 2 }}>
                    {currentPhases.length > 0 ? (
                      <Chip
                        color="primary"
                        label={`現在のフェーズ: ${currentLabels}期間（あと${daysLeftInPhase}日）`}
                      />
                    ) : (
                      <Chip variant="outlined" label="現在は評価期間の合間です" />
                    )}
                  </Box>
                  <Stepper activeStep={firstCurrentIndex} alternativeLabel sx={{ overflowX: 'auto' }}>
                    {phases.map((ph, i) => (
                      <Step key={ph.label} completed={statuses[i] === 'completed'}>
                        <StepLabel>
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              fontWeight: statuses[i] === 'current' ? 'bold' : 'normal',
                              color: statuses[i] === 'current' ? 'primary.main' : 'text.primary',
                            }}
                          >
                            {ph.label}
                            {statuses[i] === 'current' ? '（実施中）' : ''}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {formatDateJST(ph.start)} ～{' '}
                            {formatDateJST(ph.end)}
                          </Typography>
                        </StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </Box>
              );
            })()}
            {/* 評価ルートに設定されている場合のみ期限アラームを表示 */}
            {showDeadlineAlert && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                {isOverdue ? (
                  <Warning color="error" sx={{ mr: 1 }} />
                ) : isUrgent ? (
                  <Warning color="warning" sx={{ mr: 1 }} />
                ) : (
                  <CheckCircle color="success" sx={{ mr: 1 }} />
                )}
                <Typography
                  variant="body1"
                  color={isOverdue ? 'error.main' : isUrgent ? 'warning.main' : 'text.primary'}
                  sx={{ fontWeight: 'bold' }}
                >
                  {getDeadlineMessage()}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* 目標設定（被評価者） */}
        {evaluatorInfo?.is_self_evaluator && !noActivePeriod && (
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <Assignment sx={{ mr: 1, verticalAlign: 'middle' }} />
                  目標設定
                </Typography>
                {stats?.goal ? (
                  <>
                    <Chip
                      label={
                        stats.goal.approval_status === 'approved'
                          ? '承認済み'
                          : stats.goal.approval_status === 'pending_approval'
                            ? '承認待ち'
                            : stats.goal.approval_status === 'rejected'
                              ? '差し戻し'
                              : '下書き'
                      }
                      color={
                        stats.goal.approval_status === 'approved'
                          ? 'success'
                          : stats.goal.approval_status === 'pending_approval'
                            ? 'warning'
                            : stats.goal.approval_status === 'rejected'
                              ? 'error'
                              : 'default'
                      }
                      size="small"
                      sx={{ mb: 2 }}
                    />
                    {stats.goal.approval_status !== 'draft' &&
                      (stats.goal.target_description || stats.goal.achievement_criteria) && (
                        <Box sx={{ mb: 2, maxHeight: 160, overflow: 'auto' }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            目標（抜粋）
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
                            {stats.goal.target_description || '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            達成基準（抜粋）
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {stats.goal.achievement_criteria || '—'}
                          </Typography>
                        </Box>
                      )}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    目標が未登録です
                  </Typography>
                )}
                <Button variant="contained" fullWidth onClick={() => navigate(GOAL_SETTING_PATH)}>
                  目標設定へ
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* 目標承認（上長） */}
        {evaluatorInfo?.is_goal_approver_evaluator &&
          !noActivePeriod &&
          (stats?.counts.pending_goal_approvals ?? 0) > 0 && (
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <People sx={{ mr: 1, verticalAlign: 'middle' }} />
                    目標承認
                  </Typography>
                  <Typography variant="h3" color="primary" sx={{ mb: 1 }}>
                    {stats?.counts.pending_goal_approvals}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    件の目標承認が待っています
                  </Typography>
                  {stats?.flags?.can_approve_goals === false && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      目標設定期間・中期レビュー期間外のため、承認操作はできません
                    </Alert>
                  )}
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => navigate(GOAL_APPROVAL_PATH)}
                    disabled={stats?.flags?.can_approve_goals === false}
                  >
                    目標承認へ
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          )}

        {/* 自己評価 */}
        {evaluatorInfo?.is_self_evaluator && (
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <Assignment sx={{ mr: 1, verticalAlign: 'middle' }} />
                  自己評価
                </Typography>
                {stats?.goal?.approval_status !== 'approved' && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    自己評価の提出には、目標の上長承認が必要です
                  </Alert>
                )}
                {stats?.selfEvaluation ? (
                  <>
                    <Chip
                      label={getStatusLabel(stats!.selfEvaluation!.status as EvaluationStatus, stats?.goal?.approval_status as GoalApprovalStatus).label}
                      color={getStatusLabel(stats!.selfEvaluation!.status as EvaluationStatus, stats?.goal?.approval_status as GoalApprovalStatus).chipColor}
                      size="small"
                      sx={{ mb: 2 }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      最終更新: {formatDateTimeJST(stats!.selfEvaluation!.updated_at, true)}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    まだ開始されていません
                  </Typography>
                )}
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => navigate('/evaluation/self')}
                  disabled={
                    noActivePeriod ||
                    !stats?.selfEvaluation ||
                    stats?.goal?.approval_status !== 'approved'
                  }
                >
                  {stats?.selfEvaluation ? '評価を確認' : '自己評価を開始'}
                </Button>
                {noActivePeriod && !stats?.selfEvaluation && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    評価期間開始後に利用可能になります
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* 上長評価 */}
        {evaluatorInfo?.is_manager_evaluator && (stats?.counts.pending_manager_evaluations ?? 0) > 0 && (
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <People sx={{ mr: 1, verticalAlign: 'middle' }} />
                  上長評価
                </Typography>
                <Typography variant="h3" color="primary" sx={{ mb: 1 }}>
                  {stats?.counts.pending_manager_evaluations}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  件の評価が待っています
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => navigate(`/manager-evaluation/${activePeriod?.period_id}`)}
                >
                  評価を開始
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* 部門長評価 */}
        {evaluatorInfo?.is_dept_head_evaluator && (stats?.counts.pending_dept_head_evaluations ?? 0) > 0 && (
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <TrendingUp sx={{ mr: 1, verticalAlign: 'middle' }} />
                  部門長評価
                </Typography>
                <Typography variant="h3" color="secondary" sx={{ mb: 1 }}>
                  {stats?.counts.pending_dept_head_evaluations}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  件の評価が待っています
                </Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  fullWidth
                  onClick={() => navigate(getDeptHeadEvaluationListPath(activePeriod?.period_id || ''))}
                >
                  評価を開始
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* レポート */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <Assessment sx={{ mr: 1, verticalAlign: 'middle' }} />
                レポート
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                評価レポートと分析データを確認
              </Typography>
              <Button variant="outlined" fullWidth onClick={() => navigate('/reports')}>
                レポートを見る
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* HR管理 */}
        {(user?.role === 'hr' || user?.roles?.includes('hr')) && (
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <People sx={{ mr: 1, verticalAlign: 'middle' }} />
                  ユーザー管理
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  社員アカウントの管理とメンテナンス
                </Typography>
                <Button variant="contained" color="secondary" fullWidth onClick={() => navigate('/admin/users')}>
                  ユーザー管理
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* スキル・資格・プロジェクト管理カード */}
      {personalStats && (personalStats.showCards.skill || personalStats.showCards.certification || personalStats.showCards.project) && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* スキル管理カード */}
          {personalStats.showCards.skill && (
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <Assignment sx={{ mr: 1, verticalAlign: 'middle' }} />
                    スキル管理
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    {((personalStats.skillStats.notRegistered || 0) + (personalStats.skillStats.draft || 0) > 0) && (
                      <Alert severity="warning" sx={{ mb: 1 }}>
                        <Typography variant="body2">
                          未申請: {(personalStats.skillStats.notRegistered || 0) + (personalStats.skillStats.draft || 0)} 件
                        </Typography>
                        {personalStats.skillStats.notRegistered > 0 && (
                          <Typography variant="caption" display="block">
                            （未登録: {personalStats.skillStats.notRegistered}件、下書き: {personalStats.skillStats.draft}件）
                          </Typography>
                        )}
                      </Alert>
                    )}
                    {personalStats.skillStats.pending > 0 && (
                      <Alert severity="info" sx={{ mb: 1 }}>
                        承認待ち: {personalStats.skillStats.pending} 件
                      </Alert>
                    )}
                    {personalStats.skillStats.rejected > 0 && (
                      <Alert severity="error">
                        差し戻し: {personalStats.skillStats.rejected} 件
                      </Alert>
                    )}
                  </Box>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => navigate('/skills/my-skills?tab=skills')}
                  >
                    スキル管理へ
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* 資格管理カード */}
          {personalStats.showCards.certification && (
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <CheckCircle sx={{ mr: 1, verticalAlign: 'middle' }} />
                    資格管理
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    {personalStats.certificationStats.total === 0 && (
                      <Alert severity="warning" sx={{ mb: 1 }}>
                        資格が未登録です
                      </Alert>
                    )}
                    {personalStats.certificationStats.pending > 0 && (
                      <Alert severity="info" sx={{ mb: 1 }}>
                        承認待ち: {personalStats.certificationStats.pending} 件
                      </Alert>
                    )}
                    {personalStats.certificationStats.rejected > 0 && (
                      <Alert severity="error">
                        差し戻し: {personalStats.certificationStats.rejected} 件
                      </Alert>
                    )}
                  </Box>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => navigate('/skills/my-skills?tab=certifications')}
                  >
                    資格管理へ
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* プロジェクト歴管理カード */}
          {personalStats.showCards.project && (
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <TrendingUp sx={{ mr: 1, verticalAlign: 'middle' }} />
                    プロジェクト歴管理
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    {personalStats.projectStats.total === 0 && (
                      <Alert severity="warning" sx={{ mb: 1 }}>
                        プロジェクトが未登録です
                      </Alert>
                    )}
                    {personalStats.projectStats.draft > 0 && (
                      <Alert severity="warning" sx={{ mb: 1 }}>
                        未申請: {personalStats.projectStats.draft} 件
                      </Alert>
                    )}
                    {personalStats.projectStats.pending > 0 && (
                      <Alert severity="info" sx={{ mb: 1 }}>
                        承認待ち: {personalStats.projectStats.pending} 件
                      </Alert>
                    )}
                    {personalStats.projectStats.rejected > 0 && (
                      <Alert severity="error">
                        差し戻し: {personalStats.projectStats.rejected} 件
                      </Alert>
                    )}
                  </Box>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => navigate('/skills/my-skills?tab=projects')}
                  >
                    プロジェクト歴管理へ
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {/* 全体進捗状況（HR専用） */}
      {user?.roles?.includes('hr') && getOverallFunnel() && (() => {
        const of = getOverallFunnel();
        return (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h6">
                全体評価進捗状況
              </Typography>
              {orgProgress?.periods?.length > 0 && (
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <Select
                    value={orgPeriodId}
                    onChange={(e) => handleOrgPeriodChange(e.target.value)}
                    displayEmpty
                  >
                    {orgProgress.periods.map((p: any) => (
                      <MenuItem key={p.period_id} value={p.period_id}>
                        {p.period_name}{p.status === 'active' ? '（実施中）' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  進捗率（確定済み / 総対象者数）
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {Number(getProgressPercentage()).toFixed(1)}%
                </Typography>
              </Box>
              <LinearProgress variant="determinate" value={getProgressPercentage()} sx={{ height: 10 }} />
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3} md={12 / 8}>
                <Typography variant="body2" color="text.secondary">
                  総対象者数
                </Typography>
                <Typography variant="h5">{Number(of.total) || 0}</Typography>
              </Grid>
              {EVALUATION_FUNNEL_STAGES.map((stage) => (
                <Grid item xs={6} sm={3} md={12 / 8} key={stage.key}>
                  <Typography variant="body2" color="text.secondary">
                    {stage.label}
                  </Typography>
                  <Typography variant="h5">{getFunnelStageCount(of, stage.key)}</Typography>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
        );
      })()}

      {/* 上長評価待ちリスト */}
      {stats.managerEvaluations.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              上長評価待ちリスト
            </Typography>
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>社員名</TableCell>
                    <TableCell>部署</TableCell>
                    <TableCell>職級</TableCell>
                    <TableCell>ステータス</TableCell>
                    <TableCell>更新日時</TableCell>
                    <TableCell>操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.managerEvaluations.slice(0, 5).map((evaluation) => (
                    <TableRow key={evaluation.evaluation_id}>
                      <TableCell>{evaluation.employee_name}</TableCell>
                      <TableCell>{evaluation.department_name}</TableCell>
                      <TableCell>{evaluation.job_grade}</TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(evaluation.status as EvaluationStatus).label}
                          color={getStatusLabel(evaluation.status as EvaluationStatus).chipColor}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatDateTimeJST(evaluation.updated_at, true)}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            navigate(
                              getManagerEvaluationFormPath(evaluation.employee_id, activePeriod?.period_id || '')
                            )
                          }
                        >
                          評価する
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {stats.managerEvaluations.length > 5 && (
              <Button
                sx={{ mt: 2 }}
                onClick={() => navigate(getManagerEvaluationListPath(activePeriod?.period_id || ''))}
              >
                すべて表示 ({stats.managerEvaluations.length}件)
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* 部門長評価待ちリスト */}
      {stats.deptHeadEvaluations.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              部門長評価待ちリスト
            </Typography>
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>社員名</TableCell>
                    <TableCell>部署</TableCell>
                    <TableCell>職級</TableCell>
                    <TableCell>ステータス</TableCell>
                    <TableCell>更新日時</TableCell>
                    <TableCell>操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.deptHeadEvaluations.slice(0, 5).map((evaluation) => (
                    <TableRow key={evaluation.evaluation_id}>
                      <TableCell>{evaluation.employee_name}</TableCell>
                      <TableCell>{evaluation.department_name}</TableCell>
                      <TableCell>{evaluation.job_grade}</TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(evaluation.status as EvaluationStatus).label}
                          color={getStatusLabel(evaluation.status as EvaluationStatus).chipColor}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatDateTimeJST(evaluation.updated_at, true)}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            navigate(
                              getDeptHeadEvaluationFormPath(evaluation.employee_id, activePeriod?.period_id || '')
                            )
                          }
                        >
                          評価する
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {stats.deptHeadEvaluations.length > 5 && (
              <Button
                sx={{ mt: 2 }}
                onClick={() => navigate(getDeptHeadEvaluationListPath(activePeriod?.period_id || ''))}
              >
                すべて表示 ({stats.deptHeadEvaluations.length}件)
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            システム状況
          </Typography>
          <Typography variant="body1">評価システムは正常に動作しています。</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            最終更新: {formatDateTimeJST(new Date(), true)}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DashboardPage;
